import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { RichTextDirective } from '../../text/rich-text.directive';

@Component({
  selector: 'dojo-math-input',
  standalone: true,
  imports: [ReactiveFormsModule, RichTextDirective],
  templateUrl: './math-input.component.html',
  styleUrl: './math-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MathInputComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    this.initInputControlEffect();
  }

  readonly param = input.required<string>();
  readonly value = input<number>(0);
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);
  readonly step = input<number>(1);

  readonly valueChange = output<number>();

  readonly inputCtrl = new FormControl<number>(0, { nonNullable: true });

  private lastEmittedValue: number | null = null;

  private initInputControlEffect(): void {
    // Sync input value to form control
    effect(() => {
      const val = this.value();
      this.inputCtrl.setValue(val, { emitEvent: false });
      // Always sync lastEmittedValue when external value changes
      // This prevents stale comparisons when user adjusts back to a previous value
      this.lastEmittedValue = val;
    });

    // Update validators when min/max change
    effect(() => {
      const minVal = this.min();
      const maxVal = this.max();
      const validators = [Validators.required];

      if (minVal !== null) {
        validators.push(Validators.min(minVal));
      }
      if (maxVal !== null) {
        validators.push(Validators.max(maxVal));
      }

      this.inputCtrl.setValidators(validators);
      this.inputCtrl.updateValueAndValidity();
    });

    // Handle input control changes - only emit when value actually changes
    this.inputCtrl.valueChanges
      .pipe(
        tap((val) => {
          if (this.inputCtrl.valid && val !== null && val !== this.lastEmittedValue) {
            this.lastEmittedValue = val;
            this.valueChange.emit(val);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
